import React from 'react';
import classes from './LinearGauge.module.scss';
import { ApIcon } from '@alixpartners/ui-components';
import { dataVizColors } from 'styles/colors';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';

const LinearGauge = props => {
	const { values } = props;
	const {
		min,
		max,
		median,
		clientSpend,
		showFormattedSpend,
		clientSpendDirect,
		clientSpendIndirect,
		hideGradient,
		unit,
	} = values;

	const transformScale = value => {
		return ((value - min) / (max - min)) * 100;
	};

	let showMinWarning,
		showMaxWarning = false;

	const alignCliendIndicator = (
		minValue,
		maxValue,
		clientValue,
		scaledValue
	) => {
		if (clientValue > maxValue) {
			return 100;
		} else if (clientValue < minValue) {
			return 0;
		} else {
			return scaledValue;
		}
	};

	const alignCliendIndicatorLegend = (
		minValue,
		maxValue,
		clientValue,
		scaledValue
	) => {
		if (clientValue > maxValue) {
			return 100 - 1;
		} else if (clientValue < minValue) {
			return 0 + 1;
		} else {
			return scaledValue - 1;
		}
	};

	const toPrecision = (value, precision = 2) => {
		return Number.isInteger(value) ? value.toString() : value?.toFixed(0);
	};

	let scaledClientSpend = Number(transformScale(clientSpend)?.toFixed(2));
	scaledClientSpend = Math.abs(scaledClientSpend);

	let scaledClientSpendDirect = Number(
		transformScale(clientSpendDirect)?.toFixed(2)
	);
	scaledClientSpendDirect = Math.abs(scaledClientSpendDirect);

	let scaledClientSpendIndirect = Number(
		transformScale(clientSpendIndirect)?.toFixed(2)
	);
	scaledClientSpendIndirect = Math.abs(scaledClientSpendIndirect);

	let scaledMedian = Number(transformScale(median)?.toFixed(2));
	scaledMedian = Math.abs(scaledMedian);

	// const isRoot = level === 1 && group === 1;
	const isRoot = false;

	let clientMarkerPosition =
		clientSpend || clientSpend === 0
			? alignCliendIndicator(min, max, clientSpend, scaledClientSpend)
			: 'hide';
	let clientDirectMarkerPosition =
		clientSpendDirect || clientSpendDirect === 0
			? alignCliendIndicator(
					min,
					max,
					clientSpendDirect,
					scaledClientSpendDirect
			  )
			: 'hide';
	let clientIndirectMarkerPosition =
		clientSpendIndirect || clientSpendIndirect === 0
			? alignCliendIndicator(
					min,
					max,
					clientSpendIndirect,
					scaledClientSpendIndirect
			  )
			: 'hide';

	const allMarkersAtSamePosition =
		clientMarkerPosition === clientDirectMarkerPosition &&
		clientMarkerPosition === clientIndirectMarkerPosition;

	const clientMarkerAndDirectEqual =
		clientMarkerPosition === clientDirectMarkerPosition &&
		clientMarkerPosition !== clientIndirectMarkerPosition;

	const clientMarkerAndIndirectEqual =
		clientMarkerPosition === clientIndirectMarkerPosition &&
		clientMarkerPosition !== clientDirectMarkerPosition;

	const clientDirectAndIndirectEqual =
		clientDirectMarkerPosition === clientIndirectMarkerPosition &&
		clientDirectMarkerPosition !== clientMarkerPosition;

	let directPosition = '100%';
	let indirectPosition = '100%';

	let clientMarkerText = showFormattedSpend
		? formatSpendWithDecimalAndSymbol(clientSpend, '$', true)?.toUpperCase()
		: toPrecision(clientSpend, 2);
	let clientMarkerDirectText = showFormattedSpend
		? formatSpendWithDecimalAndSymbol(
				clientSpendDirect,
				'$',
				true
		  )?.toUpperCase()
		: toPrecision(clientSpendDirect, 2);
	let clientMarkerIndirectText = showFormattedSpend
		? formatSpendWithDecimalAndSymbol(
				clientSpendIndirect,
				'$',
				true
		  )?.toUpperCase()
		: toPrecision(clientSpendIndirect, 2);

	let finalMarkerText = '';

	if (allMarkersAtSamePosition) {
		directPosition = '160%';
		indirectPosition = '230%';
		finalMarkerText = `${clientMarkerText},${clientMarkerDirectText},${clientMarkerIndirectText}`;
	} else if (clientMarkerAndDirectEqual) {
		directPosition = '160%';
		indirectPosition = '100%';
		finalMarkerText = `${clientMarkerText},${clientMarkerDirectText}`;
	} else if (clientMarkerAndIndirectEqual) {
		directPosition = '100%';
		indirectPosition = '160%';
		finalMarkerText = `${clientMarkerText},${clientMarkerIndirectText}`;
	} else if (clientDirectAndIndirectEqual) {
		finalMarkerText = `${clientMarkerDirectText},${clientMarkerIndirectText}`;
		directPosition = '100%';
		indirectPosition = '160%';
	}

	//////////////////////////////////////////////////////////////////////////////////////////
	let maxValues = '';
	let minValues = '';

	if ((clientSpend || clientSpend === 0) && clientSpend > max) {
		showMaxWarning = true;
		maxValues = `Total:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(clientSpend, '$', true)?.toUpperCase()
				: toPrecision(clientSpend, 2)
		} ${unit} `;
	}
	if (
		(clientSpendDirect || clientSpendDirect === 0) &&
		clientSpendDirect > max
	) {
		showMaxWarning = true;

		maxValues = `${maxValues}direct:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(
						clientSpendDirect,
						'$',
						true
				  )?.toUpperCase()
				: toPrecision(clientSpendDirect, 2)
		} ${unit} `;
	}
	if (
		(clientSpendIndirect || clientSpendIndirect === 0) &&
		clientSpendIndirect > max
	) {
		showMaxWarning = true;
		maxValues = `${maxValues}Indirect:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(
						clientSpendIndirect,
						'$',
						true
				  )?.toUpperCase()
				: toPrecision(clientSpendIndirect, 2)
		} ${unit} `;
	}

	if ((clientSpend || clientSpend === 0) && clientSpend < min) {
		showMinWarning = true;
		minValues = `Total:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(clientSpend, '$', true)?.toUpperCase()
				: toPrecision(clientSpend, 2)
		} ${unit} `;
	}

	if (
		(clientSpendDirect || clientSpendDirect === 0) &&
		clientSpendDirect < min
	) {
		showMinWarning = true;

		minValues = `${minValues} direct:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(
						clientSpendDirect,
						'$',
						true
				  )?.toUpperCase()
				: toPrecision(clientSpendDirect, 2)
		} ${unit} `;
	}

	if (
		(clientSpendIndirect || clientSpendIndirect === 0) &&
		clientSpendIndirect < min
	) {
		showMinWarning = true;
		minValues = `${minValues} Indirect:${
			showFormattedSpend
				? formatSpendWithDecimalAndSymbol(
						clientSpendIndirect,
						'$',
						true
				  )?.toUpperCase()
				: toPrecision(clientSpendIndirect, 2)
		} ${unit} `;
	}

	let maxWarningTooltip = `Marker value( ${maxValues}) is greater than High End( ${
		showFormattedSpend
			? formatSpendWithDecimalAndSymbol(max, '$', true)?.toUpperCase()
			: toPrecision(max, 2)
	} ${unit})`;
	let minWarningTooltip = `Marker value( ${minValues}) is lesser than Low End( ${
		showFormattedSpend
			? formatSpendWithDecimalAndSymbol(min, '$', true)?.toUpperCase()
			: toPrecision(min, 2)
	} ${unit})`;

	return (
		<div
			className={`${classes.linear_gauge} ${classes.linear_gauge__marker}`}
			style={{ background: `${hideGradient && '#808080'}` }}
		>
			{(clientSpend || clientSpend === 0) && (
				<>
					<div
						className={classes.linear_gauge__indicator}
						style={{
							'--custom-marker-position': `${clientMarkerPosition}%`,
							display: `${isRoot && 'none'}`,
						}}
					/>
					<div
						className={classes.linear_gauge__spendValue}
						style={{
							'--custom-marker-position': `${alignCliendIndicatorLegend(
								min,
								max,
								clientSpend,
								scaledClientSpend
							)}%`,
							display: `${isRoot && 'none'}`,
						}}
					>
						{allMarkersAtSamePosition ||
						clientMarkerAndDirectEqual ||
						clientMarkerAndIndirectEqual
							? finalMarkerText
							: showFormattedSpend
								? formatSpendWithDecimalAndSymbol(
										clientSpend,
										'$',
										true
								  )?.toUpperCase()
								: `${toPrecision(clientSpend, 2)}${unit}`}
					</div>
				</>
			)}
			{(clientSpendDirect || clientSpendDirect === 0) && (
				<>
					<div
						className={classes.linear_gauge__indicator}
						style={{
							'--custom-marker-position': `${clientDirectMarkerPosition}%`,
							borderBottomColor: '#a52a2a',
							display: `${isRoot && 'none'}`,
							top: directPosition,
						}}
					/>
					<div
						className={classes.linear_gauge__spendValue}
						style={{
							'--custom-marker-position': `${alignCliendIndicatorLegend(
								min,
								max,
								clientSpendDirect,
								scaledClientSpendDirect
							)}%`,
							display: `${isRoot && 'none'}`,
						}}
					>
						{allMarkersAtSamePosition || clientMarkerAndDirectEqual
							? null
							: clientDirectAndIndirectEqual
								? finalMarkerText
								: showFormattedSpend
									? formatSpendWithDecimalAndSymbol(
											clientSpendDirect,
											'$',
											true
									  )?.toUpperCase()
									: `${toPrecision(clientSpendDirect, 2)}${unit}`}
					</div>
				</>
			)}
			{(clientSpendIndirect || clientSpendIndirect === 0) && (
				<>
					<div
						className={classes.linear_gauge__indicator}
						style={{
							'--custom-marker-position': `${clientIndirectMarkerPosition}%`,
							borderBottomColor: '#6495ed',
							display: `${isRoot && 'none'}`,
							top: indirectPosition,
						}}
					/>
					<div
						className={classes.linear_gauge__spendValue}
						style={{
							'--custom-marker-position': `${alignCliendIndicatorLegend(
								min,
								max,
								clientSpendIndirect,
								scaledClientSpendIndirect
							)}%`,
							display: `${isRoot && 'none'}`,
						}}
					>
						{allMarkersAtSamePosition ||
						clientMarkerAndIndirectEqual ||
						clientDirectAndIndirectEqual
							? null
							: showFormattedSpend
								? formatSpendWithDecimalAndSymbol(
										clientSpendIndirect,
										'$',
										true
								  )?.toUpperCase()
								: `${toPrecision(clientSpendIndirect, 2)}${unit}`}
					</div>
				</>
			)}
			<div
				className={classes.linear_gauge__median}
				style={{
					'--custom-median-position': `${scaledMedian}%`,
					display: `${isRoot && 'none'}`,
				}}
			/>

			{(median || median === 0) && (
				<div
					className={classes.linear_gauge__medianValue}
					style={{
						'--custom-median-position': `${
							scaledMedian === 100 ? 98 : scaledMedian - 1
						}%`,
						display: `${isRoot && 'none'}`,
					}}
				>
					{showFormattedSpend
						? formatSpendWithDecimalAndSymbol(median, '$', true)?.toUpperCase()
						: `${toPrecision(median, 2)}${unit}`}
				</div>
			)}
			{showMinWarning && (
				<div
					className={classes.warning_min}
					style={{ display: `${isRoot && 'none'}` }}
				>
					<span title={minWarningTooltip}>
						<ApIcon
							iconName="outline_error"
							iconSize="24px"
							iconColor={dataVizColors.orange}
						/>
					</span>
				</div>
			)}
			{showMaxWarning && (
				<div
					className={classes.warning_max}
					style={{ display: `${isRoot && 'none'}` }}
				>
					<span title={maxWarningTooltip}>
						<ApIcon
							iconName="outline_error"
							iconSize="24px"
							iconColor={dataVizColors.orange}
						/>
					</span>
				</div>
			)}
		</div>
	);
};

export default LinearGauge;
